import type { ClientRow } from '../context/ClientManagementContext';
import { getContactHref, isMessageSourced } from '../components/ClientProfilePanel/contact-helpers';

/** Channels the clients list can surface, highest priority first. */
const CHANNEL_PRIORITY = ['whatsapp', 'instagram', 'facebook'] as const;

export type PrimaryContactType = (typeof CHANNEL_PRIORITY)[number];

export type PrimaryContact = {
  type: PrimaryContactType;
  value: string;
  handle?: string;
  source?: string;
};

/**
 * Link to the post a client most recently commented on or reacted to, or
 * null. Instagram stores the media permalink; Facebook stores the post id
 * (`<pageId>_<postId>`), which facebook.com resolves to the post.
 */
function isPostInteraction(it: { type?: string; platform?: string }, platform: string): boolean {
  return (it.type === 'comment' || it.type === 'reaction') && it.platform === platform;
}

/** The post link for one interaction on the given platform, or null. */
function postLinkForItem(
  item: { permalink?: string; publication_id?: string },
  platform: string,
): string | null {
  if (item.permalink) return item.permalink;
  if (platform === 'facebook' && item.publication_id) {
    return `https://www.facebook.com/${item.publication_id}`;
  }
  return null;
}

/** The link of the client's most recent post interaction on the platform, or null. */
function latestPostLink(client: ClientRow, platform: string): string | null {
  const onPosts = (client.lead_interactions ?? [])
    .filter((it) => isPostInteraction(it, platform))
    .sort((a, b) => (a.captured_at < b.captured_at ? 1 : -1));
  for (const item of onPosts) {
    const link = postLinkForItem(item, platform);
    if (link) return link;
  }
  return null;
}

/**
 * Resolves the inbox/DM link for a message-sourced contact, or null.
 *
 * @param contact - The message-sourced contact.
 * @param facebookPageId - The connected Facebook Page id, when known.
 * @returns A navigable href, or null when none is known to work.
 */
function resolveMessageSourcedLink(
  contact: PrimaryContact,
  facebookPageId?: string | null,
): string | null {
  if (contact.type === 'facebook') {
    return facebookPageId
      ? `https://business.facebook.com/latest/inbox/messenger?asset_id=${facebookPageId}`
      : null;
  }
  return getContactHref('instagram', contact.value, contact.handle, contact.source);
}

/**
 * Where a Facebook/Instagram contact in the list should link to, or null.
 *
 * The contact's id is page-scoped, so it never opens a profile. Where it came
 * from decides what does work: a DM/postback opens the Page's inbox (Facebook)
 * or the DM thread (Instagram, once the handle is known); a comment/reaction
 * opens the post it was left on, falling back to the Instagram profile.
 *
 * @param client - The client row.
 * @param contact - The contact chosen by {@link resolvePrimaryContact}.
 * @param facebookPageId - The connected Facebook Page id, when known.
 * @returns A navigable href, or null when none is known to work.
 */
export function resolveContactLink(
  client: ClientRow,
  contact: PrimaryContact,
  facebookPageId?: string | null,
): string | null {
  if (contact.type === 'whatsapp') return null;
  if (isMessageSourced(contact.source)) {
    return resolveMessageSourcedLink(contact, facebookPageId);
  }
  const postLink = latestPostLink(client, contact.type);
  if (postLink) return postLink;
  return contact.type === 'instagram'
    ? getContactHref('instagram', contact.value, contact.handle, contact.source)
    : null;
}

/**
 * Picks the one contact the clients list shows for a client: WhatsApp, else
 * Instagram, else Facebook.
 *
 * A `phone` entry is deliberately not a fallback -- the contact model keeps
 * it distinct from WhatsApp, and showing it behind a wa.me link would claim a
 * channel the client may not have.
 *
 * @param client - The client row.
 * @returns The highest-priority registered contact, or null when none.
 */
export function resolvePrimaryContact(client: ClientRow): PrimaryContact | null {
  const contacts = client.contacts ?? [];
  for (const type of CHANNEL_PRIORITY) {
    const found = contacts.find((ct) => ct.type === type && ct.value);
    if (found) {
      return { type, value: found.value, handle: found.handle, source: found.source };
    }
  }
  return null;
}

/** Plain phone types, offered only when the client has no messaging channel. */
const PHONE_TYPES = ['phone', 'phone_home', 'phone_work'];

/**
 * The phone the clients list shows when no messaging channel is on file.
 *
 * A spreadsheet import stores "Celular" as a plain phone, so without this
 * fallback a 6000-client import showed an empty Contatos column for everyone
 * (2026-11-04). It is shown as text, never behind a wa.me link, so it does
 * not claim a WhatsApp the client may not have.
 *
 * @param client - The client row.
 * @returns The first stored phone, or null when there is none.
 */
export function resolvePhoneFallback(client: ClientRow): string | null {
  const found = (client.contacts ?? []).find((ct) => PHONE_TYPES.includes(ct.type) && ct.value);
  return found?.value || client.phone || null;
}
